export interface ValidationRule {
  required?: boolean;
  pattern?: RegExp;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  custom?: (value: any) => string | undefined;
  message?: string;
}

export interface ValidationRules {
  [field: string]: ValidationRule | ValidationRule[];
}

export const validateField = (value: any, rules: ValidationRule | ValidationRule[]): string | undefined => {
  const rulesArray = Array.isArray(rules) ? rules : [rules];
  
  for (const rule of rulesArray) {
    // Required validation
    if (rule.required && (!value || (typeof value === 'string' && !value.trim()))) {
      return rule.message || 'This field is required';
    }
    
    // Skip other validations if value is empty and not required
    if (!value && !rule.required) {
      continue;
    }
    
    // Pattern validation
    if (rule.pattern && typeof value === 'string' && !rule.pattern.test(value)) {
      return rule.message || 'Invalid format';
    }
    
    // Min/Max validation for numbers
    if (typeof value === 'number' || !isNaN(Number(value))) {
      const numValue = Number(value);
      if (rule.min !== undefined && numValue < rule.min) {
        return rule.message || `Value must be at least ${rule.min}`;
      }
      if (rule.max !== undefined && numValue > rule.max) {
        return rule.message || `Value must be at most ${rule.max}`;
      }
    }
    
    // Length validation for strings and arrays
    if (rule.minLength !== undefined || rule.maxLength !== undefined) {
      const length = Array.isArray(value) ? value.length : (value?.toString() || '').length;
      if (rule.minLength !== undefined && length < rule.minLength) {
        return rule.message || `Must have at least ${rule.minLength} ${Array.isArray(value) ? 'items' : 'characters'}`;
      }
      if (rule.maxLength !== undefined && length > rule.maxLength) {
        return rule.message || `Must have at most ${rule.maxLength} ${Array.isArray(value) ? 'items' : 'characters'}`;
      }
    }
    
    // Custom validation
    if (rule.custom) {
      const error = rule.custom(value);
      if (error) {
        return error;
      }
    }
  }
  
  return undefined;
};

export const validateForm = <T extends Record<string, any>>(
  values: T,
  rules: ValidationRules
): Record<string, string> => {
  const errors: Record<string, string> = {};
  
  Object.keys(rules).forEach(field => {
    const value = values[field];
    const fieldRules = rules[field];
    const error = validateField(value, fieldRules);
    
    if (error) {
      errors[field] = error;
    }
  });
  
  return errors;
};

// Debounce helper for real-time validation
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Common validation patterns
export const ValidationPatterns = {
  percentage: /^-?\d+(\.\d+)?%$/,
  amount: /^-?\d+(\.\d{1,2})?$/,
  htsCode: /^(\d{4,10}|\d{4}\*|99\d{2}\.\d{2}\.\d{2}|\d{8,10}|\d{4}\.\d{2}\.\d{4})$/,
  // RFC 5322-inspired, practical email validator:
  // - Allows common local-part characters
  // - Enforces valid domain labels (no leading/trailing hyphens, 1-63 chars per label)
  // - Requires at least one dot in the domain and permits long TLDs
  email: /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/,
  phone: /^\+?[\d\s-()]+$/,
  alphanumeric: /^[a-zA-Z0-9]+$/,
  numeric: /^\d+$/,
  decimal: /^\d+(\.\d+)?$/,
};

export const isValidEmail = (value: string): boolean => {
  if (!value) return false;
  return ValidationPatterns.email.test(value.trim());
};

// Common validation messages
export const ValidationMessages = {
  required: (field: string) => `${field} is required`,
  invalidFormat: (field: string) => `${field} has an invalid format`,
  minValue: (field: string, min: number) => `${field} must be at least ${min}`,
  maxValue: (field: string, max: number) => `${field} must be at most ${max}`,
  minLength: (field: string, min: number) => `${field} must have at least ${min} characters`,
  maxLength: (field: string, max: number) => `${field} must have at most ${max} characters`,
};